import React, { useEffect, useState } from 'react'

import classNames from 'classnames'
import { Button } from '../../../../../components/Button/Button'
import { addHtmlContentToState, getHtmlContentFromState, getTextContentFromState, parseIntoHeadlessEditor } from '../MeetingResults/Lexical/utils'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectMeetingNote,
  selectMeetingNoteIsLoading,
  selectMeetingNoteUpdateIsLoading,
  setMeetingNoteChangedExternally,
} from '../../../../../store/meetingSummary/meetingSupport'
import { showToast } from '../../../../../store/meetingSummary/display'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'

import { AddNoteToCRMModal } from '../../../../../views/MeetingSummary/CRM/V2/AddNoteToCRMModal'
import { selectMeetingInfoHTML, selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useUpdateMeetingNote } from '../hooks/api'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../../../common/copy'
import { useEffectiveConfig } from '../../../../../common/useEffectiveConfig'

import './NotepadFooter.scss'

const SAVING = 'Saving…'
const SAVED = 'Saved ✓'

export const NotepadFooter: React.FC = () => {
  const dispatch = useAppDispatch()
  const meetingNote = useAppSelector(selectMeetingNote)
  const isLoading = useAppSelector(selectMeetingNoteIsLoading)
  const isMeetingNoteUpdateLoading = useAppSelector(selectMeetingNoteUpdateIsLoading)

  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const [saveText, setSaveText] = useState<string>('')

  useEffect(() => {
    if (isMeetingNoteUpdateLoading) {
      setSaveText(SAVING)
    }
  }, [isMeetingNoteUpdateLoading])

  useEffect(() => {
    if (!saveText) {
      return
    }
    if (!isMeetingNoteUpdateLoading) {
      setSaveText(SAVED)
    }
  }, [saveText, isMeetingNoteUpdateLoading])

  useEffect(() => {
    if (!isMeetingNoteUpdateLoading) {
      const timeout = setTimeout(() => setSaveText(''), 2000)
      return () => clearTimeout(timeout)
    }
  }, [isMeetingNoteUpdateLoading])

  if (isLoading) {
    return null
  }

  const handleClick = async () => {
    const textContent = getTextContentFromState(meetingNote)
    const htmlContent = getHtmlContentFromState(meetingNote)

    const _copyContent = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }

  return (
    <div className='d-flex justify-content-between'>
      <div className='d-flex gap-2'>
        <div className={classNames('paragraph-medium', { 'saved-text': saveText === SAVED })}>{saveText}</div>
      </div>
      <div className='d-flex gap-4'>
        <div className='align-self-end'>
          <AddMeetingInfoIntoNotepadButton />
        </div>
        <div className='d-flex gap-2'>
          <IntegrationButtons />
          <Button variant='primary' disabled={!meetingNote || accountIsFetching} onClick={handleClick}>
            <i className='bi bi-copy' />
            Copy
          </Button>
        </div>
      </div>
    </div>
  )
}

const INTEGRATION_BUTTON_CONNECTORS = [
  IntegrationServiceId.REDTAIL,
  IntegrationServiceId.SALESFORCE,
  IntegrationServiceId.PRACTIFI,
  IntegrationServiceId.XLR8,
  IntegrationServiceId.HUBSPOT,
  IntegrationServiceId.WEALTHBOX,
  IntegrationServiceId.DYNAMICS,
  IntegrationServiceId.SMARTOFFICE,
  IntegrationServiceId.ELEMENTS,
  IntegrationServiceId.BLACK_DIAMOND,
  IntegrationServiceId.ENGAGE,
  IntegrationServiceId.ADVISOR_ENGINE,
]

export const IntegrationButtons = () => {
  const currentUser = useAppSelector(selectCurrentUser)
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId })

  const meetingNote = useAppSelector(selectMeetingNote)
  const htmlContent = getHtmlContentFromState(meetingNote)
  const isMeetingNoteUpdateLoading = useAppSelector(selectMeetingNoteUpdateIsLoading)

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationServiceId | null>(null)
  const { integrations: currentUserIntegrations } = currentUser ?? {}

  return (
    <>
      {!!selectedIntegration && (
        <AddNoteToCRMModal note={htmlContent} show={!!selectedIntegration} onHide={() => setSelectedIntegration(null)} serviceId={selectedIntegration} />
      )}

      {INTEGRATION_BUTTON_CONNECTORS.map((serviceId) => {
        const userHasIntegration = currentUserIntegrations?.some((i) => i.serviceId === serviceId)

        if (!userHasIntegration) {
          return null
        }

        const config = getAccountCRMIntegrationConfig(account?.data, serviceId)
        const canExport = !config || config.enableNoteExport
        const displayName = getIntegrationDisplayName(serviceId)
        if (!canExport) {
          return null
        }

        return (
          <div key={serviceId} className='d-none d-md-flex'>
            <Tooltip className='paragraph-small' title={displayName} placement='top' offset={[0, 10]}>
              <div className='d-inline-block'>
                {' '}
                <Button
                  className='zocks-p-0.75'
                  variant='header'
                  onClick={() => setSelectedIntegration(serviceId)}
                  disabled={isMeetingNoteUpdateLoading || !meetingNote}
                >
                  <IntegrationIcon serviceId={serviceId} className='crm-icon' fill='var(--zocks-gray-900)' />
                </Button>
              </div>
            </Tooltip>
          </div>
        )
      })}
    </>
  )
}
const AddMeetingInfoIntoNotepadButton = () => {
  const dispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)
  const meetingNote = useAppSelector(selectMeetingNote)
  const updateMeetingNote = useUpdateMeetingNote()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)

  const handleAddMeetingInfoClicked = async () => {
    const headlessEditor = parseIntoHeadlessEditor(meetingNote)
    addHtmlContentToState(headlessEditor, meetingInfoHTML)
    const newMeetingNote = JSON.stringify(headlessEditor.getEditorState().toJSON())
    await updateMeetingNote(newMeetingNote)
    dispatch(setMeetingNoteChangedExternally(true))
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Added to notepad!' }))
  }
  return roomSession ? (
    <Tooltip className='paragraph-small' title='Add meeting info' placement='top' offset={[0, 10]}>
      <Button className='zocks-p-0.75' onClick={handleAddMeetingInfoClicked} variant='header'>
        <i className='bi bi-info-circle-fill' />
      </Button>
    </Tooltip>
  ) : null
}
